import { useState } from 'react';
import { Plus, Check, Trash2, Edit, Bell, StickyNote, X } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { useNotas, useCrearNota, useActualizarNota, useEliminarNota, useCompletarNota } from '../../../hooks/useCalendario';
import { toast } from 'sonner';
import { formatDate } from '../../../lib/utils';

const COLORS = [
  { value: '#FBBF24', name: 'Amarillo', bg: 'bg-yellow-100', border: 'border-yellow-300' },
  { value: '#F59E0B', name: 'Naranja', bg: 'bg-orange-100', border: 'border-orange-300' },
  { value: '#EF4444', name: 'Rojo', bg: 'bg-red-100', border: 'border-red-300' },
  { value: '#22C55E', name: 'Verde', bg: 'bg-green-100', border: 'border-green-300' },
  { value: '#3B82F6', name: 'Azul', bg: 'bg-blue-100', border: 'border-blue-300' },
  { value: '#8B5CF6', name: 'Morado', bg: 'bg-purple-100', border: 'border-purple-300' },
  { value: '#EC4899', name: 'Rosa', bg: 'bg-pink-100', border: 'border-pink-300' },
  { value: '#14B8A6', name: 'Turquesa', bg: 'bg-teal-100', border: 'border-teal-300' },
];

export function NotasList() {
  const [showForm, setShowForm] = useState(false);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [formData, setFormData] = useState({ titulo: '', contenido: '', color: '#FBBF24' });

  const { data: notas = [], refetch } = useNotas();
  const crearNota = useCrearNota();
  const actualizarNota = useActualizarNota();
  const eliminarNota = useEliminarNota();
  const completarNota = useCompletarNota();

  const handleCrear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.titulo.trim()) {
      toast.error('El título es requerido');
      return;
    }
    try {
      await crearNota.mutateAsync(formData);
      toast.success('Nota creada');
      setFormData({ titulo: '', contenido: '', color: '#FBBF24' });
      setShowForm(false);
      refetch();
    } catch {
      toast.error('Error al crear nota');
    }
  };

  const handleActualizar = async (id: number, data: any) => {
    try {
      await actualizarNota.mutateAsync({ id, data });
      toast.success('Nota actualizada');
      setEditandoId(null);
      refetch();
    } catch {
      toast.error('Error al actualizar nota');
    }
  };

  const handleEliminar = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar esta nota?')) return;
    try {
      await eliminarNota.mutateAsync(id);
      toast.success('Nota eliminada');
      refetch();
    } catch {
      toast.error('Error al eliminar nota');
    }
  };

  const handleCompletar = async (id: number, completado: boolean) => {
    try {
      await completarNota.mutateAsync({ id, completado });
      refetch();
    } catch {
      toast.error('Error al actualizar nota');
    }
  };

  const getNoteStyle = (color: string) => {
    const colorObj = COLORS.find(c => c.value === color);
    return colorObj ? colorObj.bg : 'bg-yellow-100';
  };

  return (
    <div className="space-y-4">
      {!showForm && (
        <Button
          onClick={() => setShowForm(true)}
          className="bg-yellow-500 hover:bg-yellow-600 text-white"
        >
          <Plus size={16} className="mr-1" />
          Nueva Nota
        </Button>
      )}

      {showForm && (
        <Card className="p-4 border-2 border-yellow-300 bg-yellow-50/50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700">Nueva Nota</h3>
            <button
              onClick={() => setShowForm(false)}
              className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X size={16} className="text-gray-500" />
            </button>
          </div>
          <form onSubmit={handleCrear} className="space-y-3">
            <Input
              value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              placeholder="Título de la nota..."
              className="font-medium"
              autoFocus
            />
            <textarea
              value={formData.contenido}
              onChange={(e) => setFormData({ ...formData, contenido: e.target.value })}
              placeholder="Contenido de la nota..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 min-h-[80px] text-sm"
            />
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Color</label>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      formData.color === color.value
                        ? 'border-gray-800 ring-2 ring-offset-1 ring-gray-400 scale-110'
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="bg-yellow-500 hover:bg-yellow-600 text-white">
                Guardar
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
            </div>
          </form>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {notas.map((nota: any) => {
          const bgColor = getNoteStyle(nota.color || '#FBBF24');
          const isCompleted = nota.completado;

          return (
            <Card
              key={nota.id}
              className={`p-4 transition-all hover:shadow-md ${bgColor} ${
                isCompleted ? 'opacity-70' : ''
              }`}
            >
              {editandoId === nota.id ? (
                <div className="space-y-3">
                  <Input
                    defaultValue={nota.titulo}
                    onChange={(e) => { nota.titulo = e.target.value }}
                    className="font-medium bg-white/80"
                    autoFocus
                  />
                  <textarea
                    defaultValue={nota.contenido || ''}
                    onChange={(e) => { nota.contenido = e.target.value }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[60px] text-sm bg-white/80"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleActualizar(nota.id, nota)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Guardar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditandoId(null)}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  {/* Header con título y acciones */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3
                        className={`font-semibold text-gray-800 text-base truncate ${
                          isCompleted ? 'line-through' : ''
                        }`}
                      >
                        {nota.titulo}
                      </h3>
                    </div>
                    <div className="flex gap-0.5 flex-shrink-0">
                      <button
                        onClick={() => handleCompletar(nota.id, !isCompleted)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          isCompleted
                            ? 'text-green-700 hover:bg-green-200/50'
                            : 'text-gray-500 hover:bg-gray-200/50'
                        }`}
                        title={isCompleted ? 'Desmarcar' : 'Marcar como completada'}
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={() => setEditandoId(nota.id)}
                        className="p-1.5 text-blue-600 hover:bg-blue-200/50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleEliminar(nota.id)}
                        className="p-1.5 text-red-500 hover:bg-red-200/50 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Contenido */}
                  {nota.contenido && (
                    <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap line-clamp-3">
                      {nota.contenido}
                    </p>
                  )}

                  {/* Footer con metadata */}
                  <div className="flex flex-wrap items-center gap-2 mt-3 pt-2 border-t border-gray-300/50">
                    <span className="text-xs text-gray-600">
                      {formatDate(nota.createdAt)}
                    </span>
                    {nota.recordatorio && (
                      <Badge variant="default" className="bg-blue-200/70 text-blue-800 text-xs">
                        <Bell size={10} className="mr-1" />
                        Recordatorio
                      </Badge>
                    )}
                    {isCompleted && (
                      <Badge variant="success" className="text-xs bg-green-200/70 text-green-800">
                        ✓ Completada
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {notas.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
          <StickyNote size={48} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 font-medium">No hay notas</p>
          <p className="text-sm text-gray-400 mt-1">Crea tu primera nota para empezar</p>
          <Button
            onClick={() => setShowForm(true)}
            className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-white"
          >
            <Plus size={16} className="mr-1" />
            Crear Nota
          </Button>
        </div>
      )}
    </div>
  );
}